import { tr } from "../i18n/LanguageContext";
// Currency options exposed to organizers when they set a price on an event.
// A pragmatic set covering the audiences EUNOIA serves across Europe.
// Adding a new currency here is the ONLY place any UI needs to change.
export const currencies = [
  { code: "EUR", symbol: "€", label: "€ EUR (евро)" },
  { code: "SEK", symbol: "kr", label: "kr SEK (шведская крона)" },
  { code: "NOK", symbol: "kr", label: "kr NOK (норвежская крона)" },
  { code: "DKK", symbol: "kr", label: "kr DKK (датская крона)" },
  { code: "PLN", symbol: "zł", label: "zł PLN (польский злотый)" },
  { code: "CZK", symbol: "Kč", label: "Kč CZK (чешская крона)" },
  { code: "CHF", symbol: "CHF", label: "CHF (швейцарский франк)" },
  { code: "GBP", symbol: "£", label: "£ GBP (фунт)" },
  { code: "USD", symbol: "$", label: "$ USD (доллар)" },
  { code: "RUB", symbol: "₽", label: "₽ RUB (рубль)" },
];

// Pricing type options shown as a selector on the event form. Every event on
// the site resolves to exactly one of these — never to an empty/blank price.
export const PRICING_TYPES = {
  PAID: "paid",
  FREE: "free",
  DONATION: "donation",
};

export const pricingTypeOptions = [
  { value: PRICING_TYPES.PAID, label: "Платно" },
  { value: PRICING_TYPES.FREE, label: "Бесплатно" },
  { value: PRICING_TYPES.DONATION, label: "Оплата от сердца" },
];

const symbolByCode = Object.fromEntries(currencies.map((c) => [c.code, c.symbol]));

/**
 * Resolve the effective pricing type for any event — new (has pricingType) or
 * legacy (only isFree/price). This is the single source of truth: never
 * branch on isFree alone, use this so behaviour is identical everywhere.
 */
export function getEffectivePricingType(event) {
  if (!event) return PRICING_TYPES.FREE;
  if (event.pricingType) return event.pricingType;
  // Legacy events: isFree:false + numeric price → paid; anything else → free.
  if (event.isFree === false && event.price != null && Number(event.price) > 0) {
    return PRICING_TYPES.PAID;
  }
  return PRICING_TYPES.FREE;
}

/**
 * Return the user-facing price rendering for an event. Every event resolves
 * to exactly one of three strings — never empty.
 *   paid     → "€25" or "250 kr" (kronor trail, others lead)
 *   free     → "Бесплатно"
 *   donation → "Оплата от сердца"
 */
export function formatEventPrice(event) {
  const type = getEffectivePricingType(event);
  if (type === PRICING_TYPES.FREE) return tr("Бесплатно");
  if (type === PRICING_TYPES.DONATION) return tr("Оплата от сердца");
  // paid
  const num = Number(event?.price);
  if (!Number.isFinite(num)) return tr("Бесплатно");
  const code = (event?.currency || "EUR").toUpperCase();
  const sym = symbolByCode[code] || code;
  const trailing = ["SEK", "NOK", "DKK", "CZK", "PLN", "CHF"].includes(code);
  const rounded = Number.isInteger(num) ? String(num) : num.toFixed(2);
  return trailing ? `${rounded} ${sym}` : `${sym}${rounded}`;
}

/**
 * Legacy signature kept for any older call sites that pass (price, currency).
 * Prefer formatEventPrice(event) everywhere possible.
 */
export function formatPrice(price, currency) {
  return formatEventPrice({ pricingType: PRICING_TYPES.PAID, price, currency });
}
